import {
  renderTranslationString,
  TFunctionType
} from 'shared/entities/localization';
import {
  getExternalChannelKindActualName,
  VarSpecial
} from 'shared/entities/vars';

import { IRootStore } from '../../store/rootStore';
import {
  ConditionCmp,
  ConditionMode,
  mapConditionCmpToItem,
  mapConditionModeToTitle
} from '../../condition';
import {
  ActualNameCreator,
  createActualBlockName,
  normalizeCommonFields
} from '../common';

import { ExprKind, exprKindEntities } from './server';
import {
  ConditionSwitchNormalizer,
  ICase,
  IConditionSwitchParams
} from './client';

const createConditionSwitchActualNameBase = ({
  exprKind,
  cases,
  cmp,
  rootStore,
  expr,
  t
}: {
  cases: ICase[];
  rootStore: IRootStore;
  exprKind: ExprKind;
  cmp: ConditionCmp;
  expr: string | null;
  t: TFunctionType;
}): string => {
  const exprValue =
    exprKind === ExprKind.VAR && expr
      ? expr
      : exprKindEntities[exprKind].title(t);

  if (!exprValue) {
    return '';
  }

  const values = cases.map((caseItem) => {
    if (!caseItem.value) {
      return '';
    }

    if (exprKind === ExprKind.CHANNEL_KIND) {
      return (
        rootStore.pluginsStore.channelKindEntities[caseItem.value]?.title || ''
      );
    }

    if (exprKind === ExprKind.CHANNEL) {
      return (
        rootStore.channelsStore.channels.getEntity(caseItem.value)?.name || ''
      );
    }

    if (
      exprKind === ExprKind.VAR &&
      exprValue === VarSpecial.externalChannelKind
    ) {
      return getExternalChannelKindActualName({
        value: caseItem.value,
        rootStore
      });
    }

    return caseItem.value;
  });

  const separatedValues = values.join(
    ` ${mapConditionModeToTitle[ConditionMode.OR](t)} `
  );
  return `${exprValue} ${
    renderTranslationString(mapConditionCmpToItem[cmp]?.shortcut, t) || ''
  } ${separatedValues}`;
};

export const createConditionSwitchActualName: ActualNameCreator<
  IConditionSwitchParams
> = ({ params, rootStore, t }) =>
  createConditionSwitchActualNameBase({
    cases: params.cases.items,
    cmp: params.cmp.value,
    exprKind: params.data.kind,
    expr: params.data.kind === ExprKind.VAR ? params.data.expr : null,
    rootStore,
    t
  });

export const normalizeConditionSwitch: ConditionSwitchNormalizer = (
  raw,
  stores
) => {
  return {
    ...normalizeCommonFields(raw, stores),
    actualName: (t) =>
      createActualBlockName({
        paramsName: createConditionSwitchActualNameBase({
          rootStore: stores.rootStore,
          exprKind: raw.params.expr_kind,
          cmp: raw.params.cmp,
          cases: raw.params.cases,
          expr: raw.params.expr,
          t
        }),
        blockKind: raw.kind,
        blockName: raw.name,
        t,
        ...stores
      })
  };
};
